import { User } from '@/utils/auth/auth0'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import {
  useCreate,
  useInvalidate,
  useList,
  useGetIdentity,
} from '@refinedev/core'
import React, { useState, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { getAsciiName } from '../utils/textUtils'
import { adminFacingMessage } from '../utils/adminFacingError'

interface MergeRegionsModalProps {
  open: boolean
  onClose: () => void
}

interface Region {
  id: number
  name: string
  country: string | null
  region: string | null
  council: string | null
  major: string | null
  suburb: string | null
  additional_names: string[] | null
  is_public_base?: boolean
}
interface NewRegion {
  name: string
  country: string | null | undefined
  region: string | null | undefined
  council: string | null | undefined
  major: string | null | undefined
  suburb: string | null | undefined
  additional_names: string | null | undefined
}

export default function MergeRegionsModal({
  open,
  onClose: handleCloseDialog,
}: MergeRegionsModalProps) {
  const [saving, setSaving] = useState(false)
  const [regionsToMerge, setRegionsToMerge] = useState<Region[]>([])
  const [newRegion, setNewRegion] = useState<NewRegion | null>(null)
  const [mergeError, setMergeError] = useState<string | null>(null)

  const { mutate: mergeRegions } = useCreate()
  const user = useGetIdentity().data as User

  const invalidate = useInvalidate()

  const {
    formState: { errors },
    control,
    register,
    reset,
  } = useForm()

  const { data: regionOptions } = useList({
    resource: 'regions',
    meta: {
      paginate: false,
    },
    sorters: [
      {
        field: 'id',
        order: 'asc',
      },
    ],
  })

  useEffect(() => {
    if (regionsToMerge.length === 0) {
      setNewRegion(null)
    }
    if (regionsToMerge.length >= 2 && newRegion === null) {
      setNewRegion({
        name: regionsToMerge[0].name,
        country: regionsToMerge[0].country,
        region: regionsToMerge[0].region,
        council: regionsToMerge[0].council,
        major: regionsToMerge[0].major,
        suburb: regionsToMerge[0].suburb,
        additional_names: regionsToMerge[0].additional_names?.join(', '),
      })
    }
  }, [regionsToMerge])

  function handleMergeRegions(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!regionsToMerge || !user || !newRegion) {
      return
    }
    const regionIdsToReplace = regionsToMerge.map((r) => r.id)
    setSaving(true)
    setMergeError(null)

    mergeRegions(
      {
        resource: 'merge-regions',
        errorNotification: false,
        values: {
          regions_to_replace: regionIdsToReplace,
          region_to_add: newRegion,
          updated_by:
            user?.name ||
            `${user?.given_name || ''} ${user?.family_name || ''}`,
        },
      },
      {
        onSuccess: () => {
          setSaving(false)
          setRegionsToMerge([])
          setNewRegion(null)
          reset({ region_ids: [] })
          invalidate({
            resource: 'regions',
            invalidates: ['all'],
          })
        },
        onError: (error) => {
          setSaving(false)
          setMergeError(
            adminFacingMessage(error) ??
              'Error merging locations. Please try again.',
          )
        },
      },
    )
  }

  return (
    <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="xl">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          px: 2,
          py: 4,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            px: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Merge multiple locations into one new location
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
            If there are overlapping locations in the database you can use this
            tool to combine them into one new location
          </Typography>

          <Box sx={{ width: '100%' }}>
            <FormControl fullWidth>
              <Controller
                name="region_ids"
                control={control}
                defaultValue={[]}
                rules={{ required: 'At least two locations are required' }}
                render={({ field, fieldState, formState }) => (
                  <Autocomplete
                    fullWidth
                    {...field}
                    multiple
                    value={field.value || []}
                    onChange={(event, value) => {
                      field.onChange(value)
                      setRegionsToMerge([...value])
                    }}
                    options={(regionOptions?.data || []).filter(
                      (option) => !option.is_public_base,
                    )}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    filterOptions={(options, params) => {
                      const inputValue = params.inputValue.toLowerCase().trim()
                      if (!inputValue) return []
                      const asciiInput = getAsciiName(inputValue)

                      const filtered = options.filter((option) => {
                        // Check main name
                        if (option.ascii_name) {
                          if (
                            option.ascii_name.toLowerCase().includes(asciiInput)
                          ) {
                            return true
                          }
                        } else if (
                          getAsciiName(option.name).includes(asciiInput)
                        ) {
                          return true
                        }

                        // Check additional_names if they exist
                        if (
                          option.additional_names &&
                          option.additional_names.length > 0
                        ) {
                          return option.additional_names.some(
                            (additionalName: string) =>
                              getAsciiName(additionalName)
                                .toLowerCase()
                                .includes(asciiInput),
                          )
                        }

                        return false
                      })
                      return filtered
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        {option.name}
                        {option.additional_names &&
                          option.additional_names.length > 0 && (
                            <span style={{ color: 'gray', marginLeft: '8px' }}>
                              - {option.additional_names.join(', ')}
                            </span>
                          )}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search locations"
                        placeholder="Search locations..."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index })
                        return (
                          <Chip key={key} label={option.name} {...tagProps} />
                        )
                      })
                    }
                  />
                )}
              />
              {errors.region_ids && (
                <Typography color="error" variant="body2">
                  {errors.region_ids.message?.toString() ||
                    'Error adding Locations'}
                </Typography>
              )}
            </FormControl>

            {regionsToMerge.length > 0 && (
              <Box
                sx={{
                  my: 2,
                  maxHeight: '400px',
                  overflowY: 'auto',
                  border: 1,
                  borderColor: '#e5e5e5',
                  borderRadius: 2,
                }}
              >
                <Table stickyHeader sx={{ minWidth: 800 }}>
                  <TableHead
                    sx={{
                      '& .MuiTableCell-head': {
                        fontWeight: 'bold',
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Country</TableCell>
                      <TableCell>Region</TableCell>
                      <TableCell>Council</TableCell>
                      <TableCell>Major (City, Town)</TableCell>
                      <TableCell>Suburb</TableCell>
                      <TableCell>Additional Names</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {regionsToMerge.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.name}</TableCell>
                        <TableCell>{r.country || 'None'}</TableCell>
                        <TableCell>{r.region || 'None'}</TableCell>
                        <TableCell>{r.council || 'None'}</TableCell>
                        <TableCell>{r.major || 'None'}</TableCell>
                        <TableCell>{r.suburb || 'None'}</TableCell>
                        <TableCell>
                          {r.additional_names && r.additional_names.length > 0
                            ? r.additional_names.join(', ')
                            : 'None'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            px: 2,
            minWidth: 600,
          }}
          component={'form'}
          onSubmit={(e) => handleMergeRegions(e)}
        >
          <Typography variant="h6">New Location</Typography>
          {regionsToMerge.length < 2 && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
              Please select at least 2 Locations to merge
            </Typography>
          )}

          <TextField
            {...register('name', {
              required: 'This field is required',
            })}
            value={newRegion?.name || ''}
            onChange={(e) => {
              setNewRegion({
                ...newRegion,
                name: e.target.value,
              } as NewRegion)
            }}
            error={!!(errors as any)?.name}
            helperText={(errors as any)?.name?.message}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            fullWidth
            label="Name"
            disabled={regionsToMerge.length < 2}
          />
          <TextField
            {...register('country', {
              required: 'This field is required',
            })}
            value={newRegion?.country || ''}
            onChange={(e) => {
              setNewRegion({
                ...newRegion,
                country: e.target.value,
              } as NewRegion)
            }}
            error={!!(errors as any)?.country}
            helperText={(errors as any)?.country?.message}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            fullWidth
            label="Country"
            disabled={regionsToMerge.length < 2}
          />
          <TextField
            {...register('region')}
            onChange={(e) => {
              setNewRegion({
                ...newRegion,
                region: e.target.value,
              } as NewRegion)
            }}
            value={newRegion?.region || ''}
            error={!!(errors as any)?.region}
            helperText={(errors as any)?.region?.message}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            fullWidth
            label="Region"
            disabled={regionsToMerge.length < 2}
          />
          <TextField
            {...register('council')}
            value={newRegion?.council || ''}
            onChange={(e) => {
              setNewRegion({
                ...newRegion,
                council: e.target.value,
              } as NewRegion)
            }}
            error={!!(errors as any)?.council}
            helperText={(errors as any)?.council?.message}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            fullWidth
            label="Council"
            disabled={regionsToMerge.length < 2}
          />
          <TextField
            {...register('major')}
            value={newRegion?.major || ''}
            onChange={(e) => {
              setNewRegion({
                ...newRegion,
                major: e.target.value,
              } as NewRegion)
            }}
            error={!!(errors as any)?.major}
            helperText={(errors as any)?.major?.message}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            fullWidth
            label="Major (City, Town)"
            disabled={regionsToMerge.length < 2}
          />
          <TextField
            {...register('suburb')}
            value={newRegion?.suburb || ''}
            onChange={(e) => {
              setNewRegion({
                ...newRegion,
                suburb: e.target.value,
              } as NewRegion)
            }}
            error={!!(errors as any)?.suburb}
            helperText={(errors as any)?.suburb?.message}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            fullWidth
            label="Suburb"
            disabled={regionsToMerge.length < 2}
          />
          <TextField
            {...register('additional_names')}
            value={newRegion?.additional_names}
            onChange={(e) => {
              setNewRegion({
                ...newRegion,
                additional_names: e.target.value,
              } as NewRegion)
            }}
            error={!!(errors as any)?.additional_names}
            helperText={(errors as any)?.additional_names?.message}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            fullWidth
            label="Additional names"
            disabled={regionsToMerge.length < 2}
          />
          {mergeError && (
            <Alert severity="error" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
              {mergeError}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 1 }}>
            Merging Locations will delete all the locations you have selected on
            the left and replace them with the new location above.
          </Alert>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || regionsToMerge.length < 2 || !newRegion}
          >
            {saving ? 'Merging Locations...' : 'Merge locations'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  )
}
